import { requestApi } from "../shared/api";
import type { AdminModule, AdminRow } from "./data";

export interface Pagination {
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface ListResponse<T> {
  items: T[];
  pagination: Pagination;
}

export type DetailRecord = Record<string, unknown>;

const statusLabel: Record<string, string> = {
  active: "정상", suspended: "정지", withdrawn: "탈퇴",
  on_sale: "판매중", stopped: "판매중지", sold_out: "품절",
  ordered: "주문완료", confirmed: "주문확인", shipping: "배송중",
  delivered: "배송완료", completed: "확정", cancelled: "주문취소",
  requested: "견적요청", consulting: "상담중", quoted: "견적등록", paid: "결제완료",
  waiting: "답변대기", answered: "답변완료", inactive: "게시종료",
};

const formatMoney = (value: unknown) => `${Number(value || 0).toLocaleString("ko-KR")}원`;
const formatDate = (value: unknown) => value
  ? new Date(String(value)).toLocaleString("ko-KR", { timeZone: "Asia/Seoul" })
  : "-";
const genderLabel = (value: unknown) => value === "male" ? "남" : value === "female" ? "여" : "미입력";

function queryFor(module: AdminModule, values: Record<string, string>, page: number) {
  const params = new URLSearchParams({ page: String(page), pageSize: "10" });
  const q = Object.entries(values).find(([key, value]) => value && value !== "전체" && !["status", "category"].includes(key))?.[1];
  if (q) params.set("q", q);
  const statusMaps: Partial<Record<AdminModule, Record<string, string>>> = {
    members: { 정상: "active", 정지: "suspended", 탈퇴: "withdrawn" },
    products: { 판매중: "on_sale", 판매중지: "stopped", 품절: "sold_out" },
    orders: { 주문완료: "ordered", 주문확인: "confirmed", 배송중: "shipping", 배송완료: "delivered", 확정: "completed", 주문취소: "cancelled" },
    quotes: { 견적요청: "requested", 상담중: "consulting", 견적등록: "quoted", 결제완료: "paid" },
    notices: { 게시중: "active", 게시종료: "inactive" },
    inquiries: { 답변대기: "waiting", 답변완료: "answered" },
  };
  if (values.status && values.status !== "전체") params.set("status", statusMaps[module]?.[values.status] || values.status);
  if (module === "products" && values.category && values.category !== "전체") params.set("category", values.category);
  return params.toString();
}

export async function fetchModuleRows(module: AdminModule, values: Record<string, string>, page: number) {
  const response = await requestApi<ListResponse<Record<string, unknown>>>(`/${module}?${queryFor(module, values, page)}`);
  const rows = response.items.map((item, index): AdminRow => {
    const no = String(response.pagination.total - ((response.pagination.page - 1) * response.pagination.pageSize + index)).padStart(2, "0");
    if (module === "members") return {
      id: String(item.id), no, code: String(item.managementCode || ""), name: String(item.name || ""), loginId: String(item.loginId || ""),
      email: String(item.email || ""), phone: String(item.phone || "-"), gender: genderLabel(item.gender), status: statusLabel[String(item.status)] || String(item.status || ""),
    };
    if (module === "products") return {
      id: String(item.id), no, code: String(item.managementCode || ""), name: String(item.name || ""), status: statusLabel[String(item.saleStatus)] || String(item.saleStatus || ""),
      category: String(item.categoryName || ""), price: Number(item.regularPrice || 0).toLocaleString("ko-KR"), salePrice: Number(item.salePrice || 0).toLocaleString("ko-KR"), createdAt: formatDate(item.createdAt),
    };
    if (module === "orders") return {
      id: String(item.id), no, orderNo: String(item.orderNumber || ""), product: String(item.productName || ""), qty: String(item.quantity || 0),
      member: String(item.userName || ""), phone: String(item.recipientPhone || ""), address: [item.addressLine1, item.addressLine2].filter(Boolean).join(" "),
      status: statusLabel[String(item.status)] || String(item.status || ""), amount: formatMoney(item.totalAmount), method: String(item.paymentMethod || "-"),
    };
    if (module === "quotes") return {
      id: String(item.id), no, quoteNo: String(item.managementCode || ""), product: String(item.productName || ""), status: statusLabel[String(item.status)] || String(item.status || ""),
      member: String(item.userName || ""), phone: String(item.phone || "-"), purpose: String(item.purpose || ""), due: item.desiredDeliveryDate ? `${item.desiredDeliveryDate}까지` : "-", createdAt: formatDate(item.createdAt),
    };
    if (module === "notices") return {
      id: String(item.id), no, noticeNo: String(item.managementCode || ""), title: String(item.title || ""), status: statusLabel[String(item.status)] || String(item.status || ""),
      period: `${String(item.publishStartAt || "-").slice(0, 10)} ~ ${String(item.publishEndAt || "-").slice(0, 10)}`,
    };
    return {
      id: String(item.id), no, inquiryNo: String(item.managementCode || ""), title: String(item.title || ""), type: String(item.inquiryType || ""),
      member: String(item.userName || ""), phone: String(item.phone || "-"), status: statusLabel[String(item.status)] || String(item.status || ""),
    };
  });
  return { rows, pagination: response.pagination };
}

export const fetchModuleDetail = <T extends DetailRecord = DetailRecord>(module: AdminModule, id: string) => requestApi<T>(`/${module}/${id}`);
export const fetchCategories = () => requestApi<Array<{ id: string; slug: string; name: string }>>("/categories");
export const saveProduct = (payload: DetailRecord, id?: string) => requestApi<{ id: string; managementCode?: string }>(id ? `/products/${id}` : "/products", { method: id ? "PUT" : "POST", body: JSON.stringify(payload) });
export const deleteProduct = (id: string) => requestApi(`/products/${id}`, { method: "DELETE" });
export const saveNotice = (payload: DetailRecord, id?: string) => requestApi<{ id: string; managementCode?: string }>(id ? `/notices/${id}` : "/notices", { method: id ? "PUT" : "POST", body: JSON.stringify(payload) });
export const deleteNotice = (id: string) => requestApi(`/notices/${id}`, { method: "DELETE" });
export const answerInquiry = (id: string, answerBody: string) => requestApi(`/inquiries/${id}`, { method: "PUT", body: JSON.stringify({ answerBody }) });
export const updateOrder = (id: string, payload: DetailRecord) => requestApi(`/orders/${id}`, { method: "PUT", body: JSON.stringify(payload) });
export const updateQuote = (id: string, payload: DetailRecord) => requestApi(`/quotes/${id}`, { method: "PUT", body: JSON.stringify(payload) });
export const createQuoteOffer = (id: string, payload: DetailRecord) => requestApi(`/quotes/${id}/offers`, { method: "POST", body: JSON.stringify(payload) });
export const deleteQuoteOffer = (quoteId: string, offerId: string) => requestApi(`/quotes/${quoteId}/offers/${offerId}`, { method: "DELETE" });
export const addQuoteMemo = (id: string, body: string) => requestApi(`/quotes/${id}/memos`, { method: "POST", body: JSON.stringify({ body }) });
export const deleteQuoteMemo = (quoteId: string, memoId: string) => requestApi(`/quotes/${quoteId}/memos/${memoId}`, { method: "DELETE" });
export const deleteQuote = (id: string) => requestApi(`/quotes/${id}`, { method: "DELETE" });
export const updateMember = (id: string, payload: DetailRecord) => requestApi(`/members/${id}`, { method: "PUT", body: JSON.stringify(payload) });
export const addMemberMemo = (id: string, body: string) => requestApi(`/members/${id}/memos`, { method: "POST", body: JSON.stringify({ body }) });
export const resetMemberPassword = (id: string) => requestApi<{ password: string }>(`/members/${id}/password-reset`, { method: "POST" });

export async function uploadFile(file: File, ownerType: string, ownerId?: string) {
  const form = new FormData();
  form.append("file", file);
  form.append("ownerType", ownerType);
  if (ownerId) form.append("ownerId", ownerId);
  return requestApi<{ id: string; url: string; originalName?: string }>("/files", { method: "POST", body: form });
}
